import React from 'react';
import useReveal from '../../hooks/useReveal';

const PrivacyPolicy = () => {
  document.title = 'Privacy - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Privacy Policy</h1>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          1. Subject to Universal Terms of Service
        </h2>
        <p className="mb-4">
          The following PRIVACY POLICY (“Privacy Policy”) supplements and is
          subject to Cloudvantage’s (“Company”){' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Universal Terms of Use ("UToU")
          </a>
          , which are incorporated herein by this reference. Capitalized terms
          used and not otherwise defined herein shall have the meanings set
          forth in the UToU. In the event of a conflict between this Privacy
          Policy and the UToU, this Privacy Policy shall control. Customer
          acknowledges that Customer has read this Privacy Policy and Company’s{' '}
          <a
            href="/data-processing"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Data Processing Addendum
          </a>
          .
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          2. Agreement to Abide by Privacy Policy
        </h2>
        <p className="mb-4">
          BY ACCESSING THE WEBSITES OR CUSTOMER ACCOUNT, SIGNING UP FOR OR USING
          THE PRODUCTS CUSTOMER AGREES TO BE BOUND BY THIS PRIVACY POLICY AND
          THE AGREEMENTS. Company’s collection of Personal Information (as
          defined below) is required by Company to serve the Website and deliver
          the Products. Refusal to provide required Personal Information to
          Company may delay or prevent access to the Website and/or providing
          the Products.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">3. Privacy Policy</h2>
        <p className="mb-4">
          This Privacy Policy explains how and what data Company collects and
          how Company uses Personal Information (as defined herein). This
          Privacy Policy also describes options Company provides for Customer to
          access, update or otherwise take control of Personal Information that
          Company or its sub-processors process. For questions about Company’s
          practices or Customer’s rights described below, please contact
          Company’s Data Protection Team by email. By submitting a request
          Customer agrees to the terms of the Privacy Policy regarding
          Customer’s personally identifiable information. Please allow 2 to 3
          business days for a response.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          4. Changes to the Privacy Policy
        </h2>
        <p className="mb-4">
          Company reserves the right, in its sole and absolute discretion, to
          amend this Privacy Policy at any time, for any reason, which
          amendment(s) will be effective upon posting to the Website(s). The
          date of the last revision to this Privacy Policy will be indicated by
          the “Last Updated” date located at the top of this Privacy Policy. By
          accessing the Websites or Customer Account, signing up for or using
          the Products after such changes or modifications have been made shall
          constitute Customer’s acceptance of this Privacy Policy as of “Last
          Updated” date. Company may occasionally notify Customer of changes or
          modifications to this Privacy Policy by email, but is not required to
          do so. It is Customer’s responsibility to log into and keep Customer
          Account information accurate. Company assumes no liability or
          responsibility for Customer’s failure to receive an email or other
          electronic notification (i.e., through Customer Account) if such
          failure results from Customer’s failure to access Customer Account or
          related to an inaccurate email address.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          5. Information Company Collects.
        </h2>
        <h5 className="text-l font-semibold mb-4">Personal Information</h5>
        <p>
          Company may collect information (i.e., via telephone, text, email,
          paper and/or other interactions through Account, with the Websites or
          Products) when Customer provides such information on the Website(s) or
          through Customer Account, signing up for or using the Products or
          otherwise interacts with Company (collectively, "Personal
          Information") (i.e., the types of Personal Information collected may
          include, but is not limited to, Internet Protocol (IP) address, name,
          address, telephone number, birth date, billing, and delivery
          information, email address, information about Customer, credit card or
          other financial account information, or other identifying information
          about Customer):
        </p>

        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Registration.</strong> Register and manage the Account,
            including to allow access to and use of the Products; process
            payments or credits; and offer promotional programs.
          </li>
          <li>
            <strong>Improve the Website and Products.</strong> Use information
            for analytical purposes and to enable us to improve the Website and
            Products; and provide tailored and/or optimized experience.
          </li>
          <li>
            <strong>Individualization.</strong> Notify Customer about special
            offers and products or services available from Company, its
            affiliates, or partners.
          </li>
          <li>
            <strong>Communication.</strong> Communicate with Customer or
            facilitate communication between Customer and Company, its
            affiliates, or partners; conduct, monitor and record interactions
            with Company, Website and the Products; host reviews, forum posts,
            photos, videos, and other content; respond to requests, questions
            and comments.
          </li>
          <li>
            <strong>Legal Compliance.</strong> Resolve disputes or troubleshoot
            problems; prevent fraud and other potentially prohibited or unlawful
            activities; comply with relevant regulations and laws, respond to
            legal requests, prevent harm, and protect our rights; provide
            payment services, including to detect and prevent money laundering,
            fraud and security incidents, to comply with legal obligations, and
            to enforce any applicable terms of service.
          </li>
        </ol>

        <div className="mt-4 mb-6">
          <p>
            <strong>Note:</strong> If Company uses automated decision-making
            technologies to process Personal Information, Company will implement
            appropriate measures required to safeguard Customer’s rights and
            freedoms including Customer’s right to obtain human intervention.
          </p>
        </div>

        <h5 className="text-l font-semibold mb-4">Non-Personal Data</h5>
        <p>
          Company may also collect information about Customer’s browsing
          activities and history or other information that is not considered
          Personal Information through Customer’s use of and visits to the
          Website, Products or Company’s resources through a variety of
          technologies, including, but not limited to, cookies, tags, beacons,
          chatbots, and other tracking tools. Cookies may be placed by Company
          or Company’s web analytics third-party vendors or partners on the
          Websites or within the Products. For more information on the cookies,
          tracking technologies and other similar technologies visit Company{' '}
          <a
            href="/cookie-policy"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Cookie Policy
          </a>
          . Types of non-personal data collected may include, but is not limited
          to, search terms, new or returning user information, browser
          information, computer type, operating system, internet service
          providers, website usage, referring/exit pages, platform type,
          date/time stamp, number of clicks and movements within a webpage,
          advertisements viewed, among other non-personal data (collectively,
          all of the foregoing data is considered “Non-Personal Data”).
          Additionally, cookies, tags, and other tracking tools placed by web
          analytics third-party vendors or partners may collect other
          information including, but not limited to, industry and/or company
          size, among other Non-Personal Data.
        </p>

        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            Company’s use of cookies to collect information is in order to
            improve the Websites and Products.
          </li>
          <li>
            Customer can instruct a browser to refuse all cookies or to indicate
            when a cookie is being implemented..
          </li>
          <li>
            If Customer does not accept cookies, use of some features of the
            Websites and/or Products may not be available.
          </li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Do Not Track Disclosure</h5>
        <p>
          Company supports Do Not Track (“DNT”). DNT is a preference Customer
          can enable a web browser to inform websites not to track Customer’s
          information.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          6. How Personal Information is Collected
        </h2>
        <p className="mb-4">
          Company collects Personal Information by telephone, text, email, paper
          and/or other interactions by Customer with the Websites or Products.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          7. How Company Uses and Shares Personal Information.
        </h2>
        <p className="mb-4">
          Company collects Personal Information by telephone, text, email, paper
          and/or other interactions by Customer with the Websites or Products.
        </p>

        <h5 className="text-l font-semibold mb-4">Data Usage Principles</h5>
        <p className="mb-4">
          Company strongly believes in both minimizing the data collected and
          limiting its use and purpose to only that (a) for which Company has
          been given permission, (b) as necessary to deliver the Websites or
          Products, or (c) as Company might be required or permitted for legal
          compliance or other lawful purposes.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Use of Personal Information
        </h5>
        <p className="mb-4">
          Once collected, Company may use Personal Information including, but
          not limited to:
        </p>

        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            Provide service communications such as bill reminders, order
            confirmations, program registrations, and customer service messages;
          </li>
          <li>
            Respond to Customer emails or online requests for products,
            services, or information;
          </li>
          <li>Deliver and process surveys;</li>
          <li>Processing submitted job applications;</li>
          <li>
            Personalize and improve the usability of the Websites and/or
            Products;
          </li>
          <li>Fulfill and/or deliver the Products;</li>
          <li>
            Tailor content, advertising, email marketing automation and
            marketing to Customer;
          </li>
          <li>
            Share with third-parties as required by law or to protect Company as
            described herein;
          </li>
          <li>
            Share Customer’s email address as permitted as described herein;
          </li>
          <li>Communicate with Customer;</li>
          <li>
            Improve and optimize the operation and performance of the Websites
            or Products;
          </li>
          <li>
            Diagnose problems with and identify any security risks, errors, or
            needed enhancements to the Websites or Products;
          </li>
          <li>
            Detect and prevent fraud and abuse of Company’s systems, Websites or
            Products;
          </li>
          <li>
            Collecting aggregate statistics about use of the Websites or
            Products; and/or
          </li>
          <li>
            Understand and analyze Customer use of the Websites or Products.
          </li>
          <li>Create accounts to manage payment and billing information</li>
        </ol>

        <h5 className="text-l font-semibold mb-4">Transfer of Data Abroad</h5>
        <p className="mb-4">
          Utilization of the Websites or Products from a country other than the
          country where Company’s servers are located, Customer’s communications
          and interactions with Company may result in transferring Personal
          Information data across international borders. In such cases, Personal
          Information data is handled according to this Policy.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Sharing to Fulfill Service Requests and Perform Business Functions
        </h5>
        <p className="mb-4">
          Personal Information may be shared with certain third parties to
          fulfill the Customer’s service requests. Company may also share such
          information with service providers that perform business functions for
          Company. Company only provides data to service providers that have
          taken appropriate measures to protect such information under
          contractual obligations on those service providers to ensure each only
          uses Personal Information to provide for specific services.
        </p>

        <h5 className="text-l font-semibold mb-4">
          Sharing Personal Information When Legally Necessary or to Protect
          Company’s Interests.
        </h5>
        <p className="mb-4">
          Company may disclose Personal Information if required to do so by law
          or in the good-faith belief that such action is necessary to: (a)
          conform to legal requirements or comply with legal process served on
          Company, including, but not limited to, compliance with legal,
          regulatory and law enforcement requests. In such instances, Company
          cooperates with government and law enforcement agencies and private
          parties to enforce and comply with the law. Company will disclose any
          information about Customer to government or law enforcement agencies
          or private parties as Company, in Company’s sole and absolute
          discretion, believes necessary or appropriate to respond to claims and
          legal process (such as preservation requests, warrants, subpoenas or
          court orders), to protect Company’s property and rights or the
          property and rights of a third party, to protect the safety of the
          public or any person, or to prevent or stop activity Company considers
          to be illegal or unethical, among other valid legal reasons. To the
          extent Company is legally permitted to do so, Company will take
          reasonable steps to notify Customer in the event that Company is
          required to provide Customer Personal Information to third parties as
          part of the legal process; (b) protect and/or defend the rights or
          property of Company; (c) protect the personal safety of Company
          personnel or users of the public in appropriate circumstances; or (d)
          if Company and/or its assets (or a portion of its assets) are sold,
          assigned, transferred, or merged or if Company undergoes some other
          change including a change to its corporate form as part of a
          bankruptcy proceeding or otherwise, information may be transferred as
          part of that transaction or change.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          8. How to Manage the Sharing of Personal Information.
        </h2>
        <p className="mb-4">
          To access, view, or update Customer’s personal data (where available),
          Customer can sign into Customer’s Account. Customer may also request a
          deletion of personal data by emailing the Company. The request will
          apply only to the extent that it is no longer necessary for any
          Products or required for Company legitimate business purposes or legal
          or contractual record keeping requirements. Please see Company’s{' '}
          <a
            href="/data-processing"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Data Processing Addendum
          </a>{' '}
          for more information.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          9. How Long Personal Information Will Be Kept.
        </h2>
        <p className="mb-4">
          Company will keep Personal Information while Customer maintains a
          Customer Account or while Company provides the Products to Customer.
          Company will keep Personal Information for as long as is necessary:
          <ol className="list-decimal pl-6 mt-4 mb-6">
            <li>
              To respond to any questions, complaints, or claims made by
              Customer or on Customer’s behalf;
            </li>
            <li>To show that Company treated Customer fairly; or</li>
            <li>To keep records required by law.</li>
          </ol>
        </p>

        <p className="mb-4">
          2. Company will not retain Personal Information for longer than
          necessary for the purposes set out in this Policy. Different retention
          periods apply to different types of Personal Information.
        </p>

        <p className="mb-4">
          3. When Personal Information is no longer necessary to be retained,
          Company will delete or anonymize the information.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          11. Online Tracking Technologies and Advertising.
        </h2>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            Company and certain third-party service providers operating on
            Company’s behalf collect information about a user, visitor or
            Customer’s activity, or activity on devices associated with user,
            visitor or Customer, on the Websites or Products using tracking and
            analytical technologies as described in this Policy.
          </li>
          <li>
            Company may collect information whether or not a user, visitor or
            Customer is logged in or registered or maintains a Customer Account,
            and may associate this tracking data with a Customer Account, in
            which case Company will handle such information as provided in this
            Policy.
          </li>
          <li>
            Advertisers and third parties also may collect information about a
            user, visitor or Customer’s activity on the Websites and Products,
            on devices associated with a user, visitor or Customer, and on
            third-party websites and applications using tracking technologies.
            Tracking data collected by these advertisers and third parties may
            be used to decide which advertisements a user, visitor or Customer
            may see or to track activity both on or through the Websites and
            Products and on or through third-party sites and applications.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">12. Remarketing Services.</h2>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            Company uses remarketing services to advertise on third-party
            websites after visiting the Websites or Products.
          </li>
          <li>
            Company, and its third-party vendors and partners use cookies and
            other tracking tools to inform, optimize, and serve the Website and
            Products as well as advertise based on Customer’s visits to the
            Website(s) and/or use of the Products.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          13. Privacy Rights and Reporting.
        </h2>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            In addition to the rights as explained in this Policy, under
            California’s “Shine the Light” law and California Consumer Privacy
            Act (“CCPA”), and/or the European General Data Protection Rules
            (“GDPR”), Customers or Website visitors or users who provide
            personal information (as defined in the Shine the Light law, CCPA
            and/or the GDPR) using the Website or Products for personal, family,
            or household use are entitled to request and obtain from Company,
            once a calendar year, information about the Personal Information
            Company shared, if any, with other businesses for marketing uses. If
            applicable, this information would include the categories of
            personal information and the names and addresses of those businesses
            with which Company shared such information for the immediately prior
            calendar year.
          </li>
          <li>
            Company receives and responds to all data subject requests and
            grants the same rights to all, regardless of country, province or
            state of residence. The metrics reflect all requests from all
            submitters.
          </li>
          <li>Submit any date subject request to the Company</li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          14. Canada Anti-Spam Law (“CASL”) and GDPR.
        </h2>
        <p className="mb-4">
          Website visitors or users who have provided Company a Canadian or
          European Union mailing address will not receive unauthorized
          Commercial Electronic Messages (CEMs) as defined under CASL and/or
          GDPR unless these individuals have opted-in to receive CEMs. To
          access, view, update, delete or port personal data (where available),
          or to update subscription preferences, Customer can sign in to the
          Customer Account. Residents of the European Union Economic Area (EUEA)
          and to whom believe Company maintains Personal Information subject to
          the GDPR after contacting Company, may direct questions, requests or
          complaints to the UK’s Information Commissioner Office.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">15. Data Retention</h2>
        <p className="mb-4">
          Company may retain Personal Information for as long as a Customer
          Account is active or as otherwise needed to provide Customer with the
          Website or Products. Company may continue to retain such Personal
          Information even after deactivation of a Customer Account and/or cease
          to use the Website or Product, as reasonably necessary to comply with
          Company’s legal obligations, to resolve disputes regarding Customer,
          prevent fraud and/or abuse, enforce agreements and/or protect
          Company’s legitimate interests. Company maintains a data retention
          policy which applies to information in Company’s control. Subject to
          any legal requirement, if Personal Information is no longer required,
          Company will use commercially reasonable efforts that Personal
          Information is securely deleted.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">16. Age Restrictions</h2>
        <p className="mb-4">
          The Websites and Products are not intended for individuals under
          eighteen (18) years of age. No one under the age of eighteen (18)
          should provide any Personal Information to Company through the
          Websites and Products. Company does not knowingly collect Personal
          Information from individuals under eighteen (18). If reasons exist to
          believe any individual under the age of eighteen (18) has provided
          Company with any Personal Information, please contact Company at
          privacy@cloudvantage.com.ng and provide, in detail, the Personal
          Information (e.g. full name, address, telephone number) requested to
          be removed.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">17. Information Security</h2>
        <p className="mb-4">
          Company implements commercially reasonable security measures to help
          protect against unauthorized access to or unauthorized alteration,
          disclosure, or destruction of data. Company restricts access to
          Personal Information to certain companies who may require the
          information in order to operate, develop, or improve the Websites
          and/or Products. These individuals or organizations are bound by
          confidentiality obligations, agreements and may be subject to
          discipline, including termination and civil and/or criminal
          prosecution for failing to meet these obligations. Company cannot
          guarantee or warrant the security of any information Customer
          transmits to Company, or to or from the Websites and/or Products.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          18. Linking to Other Internet Websites, Applications or Platforms
        </h2>
        <p className="mb-4">
          Internet websites linked from the Websites or Products or from Company
          email or other messages may contain privacy provisions that differ
          from the provisions of this Policy. To ensure Customer privacy is
          protected, Company recommends that Customer review the privacy
          statements of these other linked websites, applications, or other
          digital platforms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          19. How You Can Access and Change Information.
        </h2>
        <p className="mb-4">
          Customer may review, update and/or correct through the Customer
          Account profile information or by emailing the Company.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          20. To View or Print a Copy of Company’s{' '}
          <a
            href="/data-processing"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Data Processing Addendum
          </a>
        </h2>
        <p className="mb-4">View or print the DPA on the website.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-l font-semibold mb-4">
          21. Company Contact Information
        </h2>
        <p className="mb-4">
          For any questions, concerns or complaints about this Policy, please
          email the Company.
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
